import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, CurrencyGbp, ShieldCheck, ArrowSquareOut, ChatCircleText, Lightning } from "@phosphor-icons/react";
import { isNativeApp, openExternalBrowser } from "../lib/nativeApp";

const PLAN_LABELS = {
  monthly: "£3.99",
  yearly: "£39.99",
  priority_monthly: "£7.99",
  priority_yearly: "£79",
};

export function Subscribe() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [billing, setBilling] = useState("monthly"); // monthly | yearly
  const [tier, setTier] = useState("standard"); // standard | priority
  const native = isNativeApp();

  const planKey = tier === "priority"
    ? (billing === "yearly" ? "priority_yearly" : "priority_monthly")
    : (billing === "yearly" ? "yearly" : "monthly");

  const start = async () => {
    if (native) {
      openExternalBrowser("https://myhandyhub.co.uk/subscribe");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan: planKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-5 py-12">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }} data-testid="subscribe-heading">Activate your membership</h1>
        <p className="text-[var(--text-muted)] mt-2">Unlimited job alerts and quoting. Cancel anytime.</p>

        {/* Billing period toggle */}
        <div className="mt-6 inline-flex bg-white border border-[var(--border)] rounded-full p-1" role="tablist" aria-label="Billing period">
          <button
            type="button"
            onClick={() => setBilling("monthly")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition ${billing === "monthly" ? "bg-[var(--brand-green)] text-white" : "text-[var(--text-muted)]"}`}
            data-testid="billing-monthly"
          >Monthly</button>
          <button
            type="button"
            onClick={() => setBilling("yearly")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition ${billing === "yearly" ? "bg-[var(--brand-green)] text-white" : "text-[var(--text-muted)]"}`}
            data-testid="billing-yearly"
          >Yearly <span className="ml-1 text-xs text-[var(--brand-orange)] font-bold">save ~17%</span></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {/* STANDARD */}
          <button
            type="button"
            onClick={() => setTier("standard")}
            className={`brand-card p-6 text-left transition ${tier === "standard" ? "ring-2 ring-[var(--brand-orange)]" : ""}`}
            data-testid="tier-standard"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-[var(--text-muted)]">Standard</div>
            </div>
            <div className="font-display font-extrabold text-3xl mt-2" style={{ color: "var(--brand-green)" }}>
              {billing === "yearly" ? "£39.99" : "£3.99"}
              <span className="text-base font-medium text-[var(--text-muted)]">/{billing === "yearly" ? "yr" : "mo"}</span>
            </div>
            <div className="text-sm text-[var(--text-muted)] mt-1">Great for part-time & side traders</div>
            <ul className="mt-4 space-y-1.5 text-sm">
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Unlimited email job alerts</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Unlimited in-app quoting</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> No commissions or success fees</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Cancel anytime</li>
            </ul>
          </button>

          {/* PRIORITY */}
          <button
            type="button"
            onClick={() => setTier("priority")}
            className={`brand-card p-6 text-left relative transition ${tier === "priority" ? "ring-2 ring-[var(--brand-orange)]" : ""}`}
            data-testid="tier-priority"
          >
            <div className="absolute -top-3 right-4 bg-[var(--brand-orange)] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Most popular · New</div>
            <div className="flex items-center gap-1.5">
              <Lightning weight="fill" className="text-[var(--brand-orange)]" size={16} />
              <div className="text-xs font-bold uppercase tracking-wider text-[var(--brand-orange)]">Priority</div>
            </div>
            <div className="font-display font-extrabold text-3xl mt-2" style={{ color: "var(--brand-green)" }}>
              {billing === "yearly" ? "£79" : "£7.99"}
              <span className="text-base font-medium text-[var(--text-muted)]">/{billing === "yearly" ? "yr" : "mo"}</span>
            </div>
            <div className="text-sm text-[var(--text-muted)] mt-1">For full-time traders who want to win jobs first</div>
            <ul className="mt-4 space-y-1.5 text-sm">
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Everything in Standard</li>
              <li className="flex items-center gap-2 font-semibold"><ChatCircleText weight="fill" className="text-[var(--brand-orange)]" /> Instant SMS alert for matching jobs</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Up to 100 SMS/month included</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Quiet hours 9pm–7am (never spammed)</li>
              <li className="flex items-center gap-2"><ShieldCheck weight="fill" className="text-[var(--brand-orange)]" /> Priority verified-badge review</li>
            </ul>
          </button>
        </div>

        <button onClick={start} disabled={busy} className="btn-accent mt-6 w-full justify-center text-base" data-testid="checkout-btn">
          {native ? (
            <><ArrowSquareOut size={18} weight="bold" /> Continue in browser</>
          ) : (
            <><CurrencyGbp size={18} weight="bold" /> {busy ? "Redirecting…" : `Pay ${PLAN_LABELS[planKey]} & activate`}</>
          )}
        </button>
        {native && (
          <p className="text-xs text-[var(--text-muted)] mt-2 text-center" data-testid="native-continue-note">
            You&apos;ll be taken to <strong>myhandyhub.co.uk</strong> to complete secure payment.
            Come back here — your dashboard unlocks automatically.
          </p>
        )}
        <p className="text-xs text-[var(--text-muted)] mt-3">
          ℹ️ Standard payment processing fees are retained by the provider and are non-refundable — this keeps our prices low and fair.
          {tier === "priority" && " SMS alerts respect UK PECR — reply STOP any time to opt out."}
        </p>
      </div>
    </Layout>
  );
}

export function BillingSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("pending");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          setTimeout(() => navigate("/dashboard/trader"), 1500);
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts.current >= 8) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts.current >= 8) { setStatus("error"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, navigate, refresh]);

  return (
    <Layout>
      <div className="max-w-md mx-auto py-20 text-center px-5">
        {status === "pending" && <>
          <p className="text-[var(--text-muted)]">Verifying your payment…</p>
        </>}
        {status === "paid" && <>
          <CheckCircle size={56} weight="fill" className="mx-auto text-[var(--brand-orange)]" />
          <h1 className="font-display font-extrabold text-2xl mt-3" style={{ color: "var(--brand-green)" }}>You&apos;re all set!</h1>
          <p className="text-[var(--text-muted)] mt-1">Your membership is active. Redirecting to your dashboard…</p>
        </>}
        {(status === "expired" || status === "timeout" || status === "error") && <>
          <h1 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>Payment not confirmed</h1>
          <p className="text-[var(--text-muted)] mt-1">Please try again or contact support.</p>
          <Link to="/subscribe" className="btn-primary mt-4 inline-flex">Try again</Link>
        </>}
      </div>
    </Layout>
  );
}

export function BillingCancel() {
  return (
    <Layout>
      <div className="max-w-md mx-auto py-20 text-center px-5">
        <h1 className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-green)" }}>Payment cancelled</h1>
        <p className="text-[var(--text-muted)] mt-1">No worries — you can activate any time.</p>
        <Link to="/subscribe" className="btn-primary mt-4 inline-flex">Try again</Link>
      </div>
    </Layout>
  );
}
